import React, { PropsWithChildren, ReactNode, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Alert, Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';

import { TooltipIconButton, toUploadFile, useHiddenFileSelect } from '@paragon/client-tools';
import { ComponentDesignUploadFile, StationComponentDesign } from '@paragon/contract-library';
import { getClientUrls, hasSupervisorFeatureFlag } from '@paragon/login-tools';
import { naturalSortByProperty } from '@paragon/utilities';

import { loadComponentDesignsIfNeeded } from '../../../actions/componentDesignActions';
import { getComponentDesignNames } from '../../../selectors/componentDesignSelectors';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ComponentDesignCard } from '../../interface/cards/ComponentDesignCard';
import { acceptedFileTypesForComponentUpload } from '../acceptedFileTypesForComponentUpload';

function AddDesignsButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <TooltipIconButton title="Add component designs" onClick={onClick} disabled={disabled}>
      <AddIcon />
    </TooltipIconButton>
  );
}

function ClearAllButton({ onClick, disabled }: { onClick: () => void; disabled?: boolean }) {
  return (
    <TooltipIconButton enableInteractive title="Clear all designs" onClick={onClick} disabled={disabled}>
      <DeleteSweepIcon />
    </TooltipIconButton>
  );
}

function LayoutSkeleton({ actionButtons, children }: PropsWithChildren<{ actionButtons?: ReactNode }>) {
  return (
    <Box sx={{ padding: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ marginRight: 'auto' }}>
          Component designs
        </Typography>

        {actionButtons}
      </Stack>

      {children}
    </Box>
  );
}

function LoadingSpinner() {
  return (
    <Box sx={{ paddingY: 2, display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );
}

export function ScheduleStationComponentDesignsList() {
  const scheduleUrl = useAppSelector(getClientUrls).scheduleUrl;

  return (
    <LayoutSkeleton>
      <Typography sx={{ color: 'text.secondary' }}>
        Use{' '}
        <Link target="_blank" sx={{ color: 'text.secondary' }} href={scheduleUrl}>
          Schedule
        </Link>{' '}
        to manage component designs available to this Station.
      </Typography>
    </LayoutSkeleton>
  );
}

interface ComponentDesignsListProps {
  readonly context: 'station' | 'assembly group';
  readonly contextGuid: string;
  readonly componentDesigns: { [guid: string]: StationComponentDesign };
  readonly onUploadComponentDesigns: (files: ComponentDesignUploadFile[]) => Promise<void>;
  readonly onUpdateComponentDesignQuantity: (guid: string, quantity: number) => void;
  readonly onClearComponentDesigns: () => void;
  readonly onRemoveComponentDesign: (guid: string) => void;
}

export function ComponentDesignsList(props: ComponentDesignsListProps) {
  const { context, contextGuid, componentDesigns } = props;

  const dispatch = useAppDispatch();

  const loadingStation = useAppSelector(
    (state) => context === 'station' && state.stations.fullyLoadedStations[contextGuid] !== true,
  );
  const loadingAssemblyGroup = useAppSelector(
    (state) => context === 'assembly group' && !(contextGuid in state.assemblyGroups.data),
  );
  const componentDesignGuids = Object.keys(componentDesigns);

  useEffect(() => {
    dispatch(loadComponentDesignsIfNeeded(componentDesignGuids));
  }, [componentDesignGuids, dispatch]);

  const componentDesignsStatus = useAppSelector((state): 'loading' | 'error' | 'good' => {
    // We don't even know what component designs are in the station/assembly group yet...
    if (loadingStation || loadingAssemblyGroup) return 'loading';

    if (componentDesignGuids.some((guid) => guid in state.componentDesigns.designsFailedToLoad)) return 'error';
    if (componentDesignGuids.some((guid) => guid in state.componentDesigns.designsBeingLoaded)) return 'loading';

    return 'good';
  });

  if (componentDesignsStatus === 'error') {
    return (
      <LayoutSkeleton>
        <Alert severity="error">Unable to load component designs.</Alert>
      </LayoutSkeleton>
    );
  }

  if (loadingStation || componentDesignsStatus === 'loading') {
    return (
      <LayoutSkeleton>
        <LoadingSpinner />
      </LayoutSkeleton>
    );
  }

  return <LoadedComponentDesignsList {...props} />;
}

function LoadedComponentDesignsList({
  context,
  componentDesigns,
  onUploadComponentDesigns: uploadComponentDesigns,
  onClearComponentDesigns: clearComponentDesigns,
  onUpdateComponentDesignQuantity: updateComponentDesignQuantity,
  onRemoveComponentDesign: removeComponentDesign,
}: ComponentDesignsListProps) {
  const isSupervisor = useAppSelector(hasSupervisorFeatureFlag);

  const [renderHiddenFileSelect, selectFilesToUpload] = useHiddenFileSelect(acceptedFileTypesForComponentUpload, true);

  const componentDesignNames = useAppSelector(getComponentDesignNames);

  const sortedComponentDesigns = naturalSortByProperty(
    Object.entries(componentDesigns),
    ([componentDesignGuid]) => componentDesignNames[componentDesignGuid],
  );

  async function handleUploadRequested() {
    const chosenFiles = await selectFilesToUpload();
    const formattedUploadFiles: ComponentDesignUploadFile[] = chosenFiles.map(toUploadFile);
    await uploadComponentDesigns(formattedUploadFiles);
  }

  function handleRemoveAllDesigns() {
    clearComponentDesigns();
  }

  return (
    <>
      {renderHiddenFileSelect()}

      <LayoutSkeleton
        actionButtons={
          isSupervisor && (
            <>
              <AddDesignsButton onClick={handleUploadRequested} />
              <ClearAllButton onClick={handleRemoveAllDesigns} disabled={Object.keys(componentDesigns).length === 0} />
            </>
          )
        }
      >
        {sortedComponentDesigns.length > 0 ? (
          <Stack spacing={1}>
            {sortedComponentDesigns.map(([componentDesignGuid]) => (
              <ComponentDesignCard
                interactive={isSupervisor}
                key={componentDesignGuid}
                context={context}
                componentDesignGuid={componentDesignGuid}
                quantity={componentDesigns[componentDesignGuid].quantity}
                onUpdateComponentDesignQuantity={(quantity) =>
                  updateComponentDesignQuantity(componentDesignGuid, quantity)
                }
                onRemoveComponentDesign={() => removeComponentDesign(componentDesignGuid)}
              />
            ))}
          </Stack>
        ) : (
          <Stack spacing={1} direction="column">
            <Typography sx={{ color: 'text.secondary' }}>
              There are currently no component designs in this {context}.
            </Typography>

            {isSupervisor && (
              <Button variant="outlined" onClick={handleUploadRequested}>
                Upload component designs
              </Button>
            )}
          </Stack>
        )}
      </LayoutSkeleton>
    </>
  );
}
